// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';

import EditableMulti from '../EditableMulti';
import Modal from './Modal';
import api from '../../api';

const OPTIONS_DATATYPE = {
    "textbox": "Text (single-line)",
    "textbox-half": "Text (half-line)",
    "textarea": "Paragraph",
    "date": "Date",
    "dropdown": "Dropdown",
    "checkbox": "Checkbox"
};

export default class EditForm extends Modal {

    state = {
        form: {
            title: ""
        }
    }

    componentDidMount = async () => {
        const data = this.props.data;
        const form = data ? data.form : null;

        if (!form || !form._id) {
            toast.error("Invalid form - something went wrong.");
            return this.props.modal("");
        }

        await this.props.loading(1005);
        const res = await api.get("/form/get?formId=" + form._id);
        if (res && res.form)
            this.setState({ ...res.form });
        else toast.error("Unable to load form - something went wrong.");

        this.props.loaded();
    }

    editSection = (idx, diff) => {
        let sections = [ ...this.state.sections ];
        sections[idx] = {
            ...sections[idx],
            ...diff
        };
        this.setState({ sections });
    }

    deleteSection = (idx) => {
        let sections = [ ...this.state.sections ];
        sections.splice(idx,1);
        this.setState({ sections });
    }

    editField = (s, f, diff) => {
        let fields = [ ...this.state.sections[s].fields ];
        fields[f] = {
            ...fields[f],
            ...diff
        };
        this.editSection(s, { fields });
    }

    deleteField = (s, f) => {
        let fields = [ ...this.state.sections[s].fields ];
        fields.splice(f,1);
        this.editSection(s, { fields });
    }

    renderContent = () => {
        const f = this.state;

        return (<div className="modal-content modal-center">
            
            <h1>Edit Form: <strong>{this.props.data.form.title}</strong></h1>

            <div className="form-edit-container">

                <label>Form Title</label>
                <div className="form-edit-inline">
                    <input
                        className="form-input-text"
                        type="text"
                        value={f.title}
                        onChange={e => this.setState({ title: e.target.value })}
                        placeholder="Form title..."
                    />
                </div>

                <label>Description</label>
                <div className="form-edit-inline">
                    <textarea
                        className="form-input-textarea"
                        value={f.description}
                        onChange={e => this.setState({ description: e.target.value })}
                        placeholder="Description..."
                    />
                </div>
                
                <div style={{ clear: 'both', height: 10 }} />
                <h2>Form Sections</h2>
                <p>
                    Form sections are displayed like pages in a web form. Users enter of review a section of a form and click<br />
                    on <strong>Next</strong> to proceed through the form sections.
                    Click on the <strong>＋Add New Section</strong> button below to add new sections to the form, or <strong>＋Add New Field</strong> within a section to add fields to the section.
                </p>

                {/* Sections */}
                {f.sections ? f.sections.map((s,idx) => <React.Fragment key={idx}>
                    <hr />
                    
                    <label>Section Title</label>
                    <div className="form-edit-inline">
                        <input
                            className="form-input-text"
                            type="text"
                            value={s.title}
                            onChange={e => this.editSection(idx, { title: e.target.value })}
                            placeholder="Section title..."
                        />
                    </div>

                    <label>Description</label>
                    <div className="form-edit-inline">
                        <textarea
                            className="form-input-textarea"
                            value={s.description}
                            onChange={e => this.editSection(idx, { description: e.target.value })}
                            placeholder="Section description..."
                        />
                    </div>
                    
                    <div style={{ clear: 'both', height: 10 }} />

                    <h3 className="form-edit-field-header">Section Fields</h3>
                    <div
                        className="form-edit-section-delete"
                        onClick={() => this.deleteSection(idx)}
                    >Delete section</div>

                    {s.fields.map((field,jdx) => <React.Fragment key={String(idx) + "-" + String(jdx)}>
                        <div className="form-edit-field">
                            
                            <div className="form-edit-field-line">
                                <div className="form-edit-field-half">
                                    <label>Field Label</label>
                                    <div className="form-edit-inline">
                                        <input
                                            className="form-input-text-half"
                                            type="text"
                                            value={field.label}
                                            onChange={e => this.editField(idx, jdx, { label: e.target.value })}
                                            placeholder={"Form field label"}
                                        />
                                    </div>
                                </div>

                                <div className="form-edit-field-half">
                                    <label>Field Key</label>
                                    <div className="form-edit-inline">
                                        <input
                                            className="form-input-text-half"
                                            type="text"
                                            value={field.key}
                                            onChange={e => this.editField(idx, jdx, { key: e.target.value })}
                                            placeholder={"Document field key"}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-edit-field-line">
                                <div className="form-edit-field-half">
                                    <label>Input Type</label>
                                    <div className="form-edit-inline">
                                        <Select
                                            className="select-container"
                                            classNamePrefix="select"
                                            value={(field.dataType in OPTIONS_DATATYPE) ? {
                                                value: field.dataType,
                                                label: OPTIONS_DATATYPE[field.dataType]
                                            } : {
                                                value: "",
                                                label: "Select an option..."
                                            }}
                                            options={Object.keys(OPTIONS_DATATYPE).map(key => ({
                                                label: OPTIONS_DATATYPE[key],
                                                value: key
                                            }))}
                                            isSearchable={true}
                                            placeholder="Select an option..."
                                            onChange={option => this.editField(idx, jdx, { dataType: option.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-edit-field-half">
                                    <label>Required</label>
                                    <div className="form-edit-inline">
                                        <Select
                                            className="select-container"
                                            classNamePrefix="select"
                                            value={field.required ? ({
                                                value: "true",
                                                label: "Input is required"
                                            }) : ({
                                                value: "false",
                                                label: "Input is optional"
                                            })}
                                            options={[{
                                                value: "true",
                                                label: "Input is required"
                                            }, {
                                                value: "false",
                                                label: "Input is optional"
                                            }]}
                                            isSearchable={false}
                                            placeholder="Select an option..."
                                            onChange={option => this.editField(idx, jdx, { required: option.value === "true" })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-edit-field-line" style={{ marginRight: 0 }}>
                                <label>Hint <span style={{ fontSize: 12, color: "rgb(160,160,160)" }}>(Optional)</span></label>
                                <div className="form-edit-inline">
                                    <input
                                        className="form-input-text"
                                        type="text"
                                        value={field.hint}
                                        onChange={e => this.editField(idx, jdx, { hint: e.target.value })}
                                        placeholder="Instructions for user to understand this field..."
                                    />
                                </div>
                            </div>

                            {/* Add options */}
                            {field.dataType === "dropdown" ? <>
                                <label>Options</label>
                                <div className="form-edit-inline">
                                    <EditableMulti
                                        selectClassName="config-multi-select-container"
                                        selectClassPrefix="config-multi-select"
                                        placeholder="Enter dropdown options..."
                                        options={[]}
                                        hideMenu={true}
                                        value={field.options ? field.options.map(f => ({
                                            value: f,
                                            label: f
                                        })) : []}
                                        onChange={(values) => this.editField(idx, jdx, {
                                            options: values ? values.map(v => v.value) : []
                                        })}
                                    />
                                </div>
                            </> : ""}

                            <div
                                className="form-edit-field-delete"
                                onClick={() => this.deleteField(idx, jdx)}
                            >Delete field</div>

                            <div style={{ clear: 'both' }} />
                        </div>
                        <div className="form-edit-field-separator" />
                    </React.Fragment>)}

                    {/* Add new field? */}
                    <div
                        className="form-add-section"
                        onClick={() => this.editSection(idx, {
                            fields: [
                                ...s.fields,
                                {
                                    label: "",
                                    key: "",
                                    required: false,
                                    hint: "",
                                    options: [],
                                    dataType: "textbox"
                                }
                            ]
                        })}
                    >＋ Add New Field</div>

                </React.Fragment>) : ""}

                <hr />

                {/* Add new section? */}
                <div
                    className="form-add-section"
                    onClick={() => this.setState({
                        sections: [
                            ...(f.sections ? f.sections : []),
                            {
                                title: "My New Section",
                                description: "",
                                fields: []
                            }
                        ]
                    })}
                >＋ Add New Section</div>

            </div>

            {/* Save form */}
            <div style={{ clear: 'both', height: 40 }} />
            <div
                className="button"
                style={{
                    float: 'none',
                    backgroundImage: "url('https://static.stax.ai/images/icons/reviewed.png')",
                }}
                onClick={async () => {
                    const f = this.state;
                    if (!f.title || f.title === "")
                        return toast.error("Form title cannot be empty!");

                    await this.props.saveForm(f);
                    this.props.modal();
                }}
            >Save and Close</div>

        </div>);
    }

};